import Account from "nexia_mobile/models/account";
import BaseModel from "nexia_mobile/models/base_model";
import AlertCollection from "nexia_mobile/models/alert_collection";
import BaseCollection from "nexia_mobile/models/base_collection";
import LocationCollection from "nexia_mobile/models/location_collection";
import NewsCollection from "nexia_mobile/models/news_collection";
import PhoneCollection from "nexia_mobile/models/phone_collection";
import Location from "nexia_mobile/models/location";
import { ResourceMap } from "nexia_mobile/framework/resource";
import LocalSettings from "nexia_mobile/models/local_settings";
import Platform from "nexia_mobile/platform";
import MediaType from "nexia_mobile/framework/media_type";
import Geofence from "nexia_mobile/models/geofence";
import Choice from "nexia_mobile/models/choice";
import Dispatch from "nexia_mobile/flux_like/dispatch";
import Store, { setValueAtPath } from "nexia_mobile/flux_like/store";
import Environment from "nexia_mobile/environment";
import EventStream from "nexia_mobile/event_stream";

export default class Session extends BaseModel {
  nestedCollections() {
    return {
      locations: {
        collection: LocationCollection,
      },
      alerts: {
        collection: AlertCollection,
      },
      news: {
        collection: NewsCollection,
      },
      geofences: {
        model: Geofence,
        collection: BaseCollection,
      },
      choices: {
        model: Choice,
        collection: BaseCollection,
      },
      phones: {
        collection: PhoneCollection,
      },
    };
  }

  get hasNews() {
    return this.get("hasNews");
  }
  set hasNews(value) {
    return this.set("hasNews", value);
  }

  get hasAlerts() {
    return this.get("hasAlerts");
  }
  set hasAlerts(value) {
    return this.set("hasAlerts", value);
  }

  get hasChoices() {
    return this.get("hasChoices");
  }
  set hasChoices(value) {
    return this.set("hasChoices", value);
  }

  constructor(...args) {
    super(...args);
    this.listenTo(this.get("alerts"), "sync", this._handleUnacknowledgedAlerts);
  }
  initialize() {
    super.initialize();
    // We need a valid URL to pull ourselves out of the session cache
    this._updateUrl();
  }

  sync(...args) {
    // Update our URL in case the host has changed
    this._updateUrl();
    return super.sync(...args);
  }

  populate() {
    this.populateStartedAt = Date.now();
    // try to load session and location from cache first
    if (!LocalSettings.getShowVognitionOnStartup()) {
      this._loadFromCache();
    }
    return this._fetchFromServer().then(() => {
      EventStream.start(this.get("eventStream"));
    });
  }

  onParse(attrs) {
    // This property gets added when we're placed in the session cache. Reset
    // it before parsing since re-parsing means we've fetched the real session.
    this.set("cached", false);
    let parsedAttrs = super.onParse(attrs);
    return {
      ...parsedAttrs,
      canControlAutomations: parsedAttrs.can_control_automations,
      canManageLocks: parsedAttrs.can_manage_locks,
      canViewVideos: parsedAttrs.can_view_videos,
      canReceiveNotification: parsedAttrs.can_receive_notifications,
      isActivatedByActivationCode: parsedAttrs.is_activated_by_activation_code,
    };
  }

  parseLinksChild(links) {
    let foundLocations = [];

    links.forEach((link) => {
      let itemType = link.getCollectionItemType();
      if (itemType === MediaType.ALERT) {
        this.hasAlerts = true;
        // Set showAlertsBadge at the startup based on has_unacknowledged
        setValueAtPath("showAlertsBadge", link.getData().has_unacknowledged);
        this.get("alerts").setUrl(link.href);
      } else if (itemType === MediaType.NEWS) {
        this.get("news").set(link.getData(), { parse: true });
        this.hasNews = this.get("news").length > 0;
      } else if (link.hasType(MediaType.LOCATION)) {
        // Jump through hoops to try to find an existing Location
        // model and just set attributes on it, if one is available.
        let location = ResourceMap.findByHref(link.href);
        if (location) {
          let data = link.getData();
          delete data.id;
          location.set(data, { parse: true });
        } else {
          location = new Location(link.getData(), { parse: true });
        }
        foundLocations.push(location);
      } else if (itemType === MediaType.GEOFENCE) {
        this.get("geofences").set(link.getData(), { parse: true });
      } else if (itemType === MediaType.CHOICE) {
        this.hasChoices = true;
        this.get("choices").set(link.getData(), { parse: true });
      } else if (itemType === MediaType.PHONE) {
        this.get("phones").set(link.getData(), { parse: true });
      }
    });

    return this.get("locations").set(foundLocations);
  }

  parseLinksSelf(links) {
    let result = super.parseLinksSelf(links);
    let host = LocalSettings.getHost();
    if (this.id.indexOf(host) !== 0 && !Environment.isTestRunner()) {
      console.warn(
        `Host is set to ${host} in LocalSettings, but the Session retrieved from the server has an href of ${this.id}. Session caching will not work unless the server returns the same host as is set in LocalSettings.`
      );
    }
    return result;
  }

  parseLinksParent(links) {
    let link = links && links[0];
    if (link && link.hasType(MediaType.ACCOUNT)) {
      const existingAccount = this.get("account");
      if (existingAccount != null) {
        existingAccount.set(existingAccount.parse(link.getData()));
      } else {
        const newAccount = new Account(link.getData(), { parse: true });
        this.set("account", newAccount);
      }
    }
  }

  parseLinksStream(links) {
    const link = links[0];
    if (!link) {
      return;
    }
    this.set("eventStream", link);
  }

  hasMultipleLocations() {
    return this.get("locations").length > 1;
  }

  hasUnreadNews() {
    if (this.get("cached")) {
      return false;
    }

    return this.get("news").any((newsItem) => newsItem.get("unread"));
  }

  _handleUnacknowledgedAlerts(collection) {
    let alerts = collection.filter((alert) => {
      return !alert.get("acknowledged");
    });
    // Set showAlertsBadge if there are unacknowledged alerts
    setValueAtPath("showAlertsBadge", alerts.length > 0);
  }

  _loadFromCache() {
    let cache = Platform.getSessionCache();
    let cachedSession = cache[this.url()];
    if (cachedSession) {
      this.set(this.parse(cachedSession));

      if (Store.session === this) {
        let location = this._getDefaultLocation();
        let cachedLocation = cache[location.url()];
        if (cachedLocation) {
          location.preventHomeProfileUpdate();
          location.set(location.parse(cachedLocation));
          location.allowHomeProfileUpdate();
          Dispatch("LOCATION_CHANGE", location);
        }
      }
    }
  }

  _fetchFromServer() {
    return this.fetch({
      type: "POST",
      data: {
        /* eslint-disable camelcase */
        ...Platform.getAppInfo(),
        tracked_settings: LocalSettings.getTrackedSettings(),
        /* eslint-enable camelcase */
      },
      postData: true,
      useBackgroundLoadingIndicator: true,
    }).then((sessionResponse) => {
      this._addToSessionCache(sessionResponse);

      let location = this._getDefaultLocation();

      if (Store.session === this) {
        return Promise.all([
          location
            .fetch({
              useBackgroundLoadingIndicator: true,
            })
            .done((locationResponse) => {
              location.populatePendingRequestsRecursively();
              this._addToSessionCache(locationResponse);
              Dispatch("LOCATION_CHANGE", location);
            }),
          this.get("account").fetch(),
        ]);
      } else {
        return Promise.resolve();
      }
    });
  }

  _addToSessionCache(response) {
    Platform.addToSessionCache(response);
  }

  _getDefaultLocation() {
    let id = this._defaultLocationId();
    let location = this.get("locations").get(id);
    return location || this.get("locations").first();
  }

  _defaultLocationId() {
    return LocalSettings.getDefaultLocationId();
  }

  _updateUrl() {
    let host = LocalSettings.getHost();
    this.setUrl(`${host}/mobile/session`);
  }
}



// WEBPACK FOOTER //
// ./src/models/session.js